import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Search, Users, UserPlus, UsersRound, Sparkles, Download, Send } from "lucide-react";
import { api } from "../lib/api";
import { STAGES, FAITH_STATUSES, DBS_TYPES, stageLabel, dbsTypeLabel, faithLabel } from "../lib/constants";
import { Modal } from "../components/Modal";
import { toCsv, downloadCsv, dateStamp } from "../lib/csv";

interface Person {
  id: string; name: string; email: string | null; phone: string | null;
  source: string | null; pathway_stage: string; faith_status: string;
  group_id: string | null; group_name?: string;
  assigned_leader_id?: string | null; leader_name?: string | null;
  dbs_type?: string | null;
  origin?: "self" | "campaign";
  oikos?: string | null;
  introduced_by_person_id?: string | null; introduced_by_name?: string | null;
  baptised_at?: number | null;
  baptism_story?: string | null;
  notes?: string | null;
}

interface Group { id: string; name: string }
interface Leader { id: string; name: string }

export default function People() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [people, setPeople] = useState<Person[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [leaders, setLeaders] = useState<Leader[]>([]);
  // Initialise filters from the URL so deep links from the Dashboard work
  // (e.g. /people?stage=active comes in pre-filtered).
  const [q, setQ] = useState(searchParams.get("q") ?? "");
  const [stage, setStage] = useState(searchParams.get("stage") ?? "");
  const [status, setStatus] = useState(searchParams.get("status") ?? "");
  const [dbsType, setDbsType] = useState(searchParams.get("dbs_type") ?? "");
  const [origin, setOrigin] = useState<"" | "self" | "campaign">(
    (searchParams.get("origin") as any) ?? "",
  );
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Person | null>(null);

  // Mirror filter state back into the URL so the view is shareable / refreshable.
  useEffect(() => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (stage) params.set("stage", stage);
    if (status) params.set("status", status);
    if (dbsType) params.set("dbs_type", dbsType);
    if (origin) params.set("origin", origin);
    setSearchParams(params, { replace: true });
  }, [q, stage, status, dbsType, origin, setSearchParams]);

  const load = async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (stage) params.set("stage", stage);
    if (status) params.set("status", status);
    if (dbsType) params.set("dbs_type", dbsType);
    if (origin) params.set("origin", origin);
    const [p, g, l] = await Promise.all([
      api.get<{ people: Person[] }>(`/people?${params}`),
      api.get<{ groups: Group[] }>("/groups"),
      api.get<{ leaders: Leader[] }>("/leaders"),
    ]);
    setPeople(p.people); setGroups(g.groups); setLeaders(l.leaders);
  };
  useEffect(() => { load(); }, [q, stage, status, dbsType, origin]);

  const counts = {
    total: people.length,
    new_invited: people.filter((p) => p.pathway_stage === "outreach" || p.pathway_stage === "invited").length,
    in_groups: people.filter((p) => p.group_id).length,
    exploring: people.filter((p) => p.faith_status === "exploring").length,
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">People</h1>
          <p className="text-ink-500 text-sm">Manage people from outreach to multiplication</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="btn-secondary"
            onClick={() => {
              const csv = toCsv(
                people.map((p) => ({
                  ...p,
                  pathway_stage: stageLabel(p.pathway_stage),
                  faith_status: faithLabel(p.faith_status),
                  dbs_type: dbsTypeLabel(p.dbs_type),
                  group: p.group_name ?? "",
                  leader: p.leader_name ?? "",
                  origin: p.origin === "campaign" ? "Campaign referral" : "Self-generated",
                })),
                [
                  { key: "name",          header: "Name" },
                  { key: "phone",         header: "Phone" },
                  { key: "email",         header: "Email" },
                  { key: "source",        header: "Source" },
                  { key: "origin",        header: "Origin" },
                  { key: "pathway_stage", header: "Pathway Stage" },
                  { key: "faith_status",  header: "Faith Status" },
                  { key: "dbs_type",      header: "DBS Type" },
                  { key: "group",         header: "Group" },
                  { key: "leader",        header: "Assigned Leader" },
                  { key: "notes",         header: "Notes" },
                ],
              );
              downloadCsv(`people-${dateStamp()}.csv`, csv);
            }}
            disabled={people.length === 0}
            title="Download current view as CSV"
          >
            <Download className="h-4 w-4" /> <span className="hidden sm:inline">Export CSV</span>
          </button>
          <button className="btn-primary" onClick={() => setAdding(true)}><Plus className="h-4 w-4" /> Add Person</button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="relative lg:col-span-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none" />
          <input className="input pl-9" placeholder="Search name, phone, source…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <select className="input" value={stage} onChange={(e) => setStage(e.target.value)}>
          <option value="">All Pathway Stages</option>
          {STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <select className="input" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All Faith Status</option>
          {FAITH_STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <select className="input" value={dbsType} onChange={(e) => setDbsType(e.target.value)}>
          <option value="">All DBS Types</option>
          {DBS_TYPES.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
        </select>
      </div>

      {/* Origin pills — distinguish self-generated from campaign-generated contacts */}
      <div className="flex flex-wrap gap-2 items-center text-sm">
        <span className="text-xs text-ink-500 mr-1">Origin:</span>
        <OriginPill active={origin === ""} onClick={() => setOrigin("")} label="All" />
        <OriginPill active={origin === "self"} onClick={() => setOrigin("self")}
          label="Self-generated" tint="ring-ink-300 text-ink-700 data-[active=true]:bg-ink-900 data-[active=true]:text-white data-[active=true]:ring-ink-900" />
        <OriginPill active={origin === "campaign"} onClick={() => setOrigin("campaign")}
          label="Campaign referrals" tint="ring-brand-100 text-brand data-[active=true]:bg-brand data-[active=true]:text-white data-[active=true]:ring-brand" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Mini label="Total People" value={counts.total} icon={<Users className="h-5 w-5" />} />
        <Mini label="New & Invited" value={counts.new_invited} accent="text-blue-700" icon={<UserPlus className="h-5 w-5" />} />
        <Mini label="In Groups" value={counts.in_groups} accent="text-green-700" icon={<UsersRound className="h-5 w-5" />} />
        <Mini label="Exploring Jesus" value={counts.exploring} accent="text-amber-700" icon={<Sparkles className="h-5 w-5" />} />
      </div>

      {/* Desktop table */}
      <div className="card overflow-hidden hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-ink-50 text-left text-xs font-medium uppercase text-ink-500">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Contact</th>
              <th className="px-4 py-3">Source</th>
              <th className="px-4 py-3">Pathway Stage</th>
              <th className="px-4 py-3">Faith Status</th>
              <th className="px-4 py-3">DBS Type</th>
              <th className="px-4 py-3">Group</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {people.map((p) => (
              <tr key={p.id} className="hover:bg-ink-50/60 cursor-pointer" onClick={() => setEditing(p)}>
                <td className="px-4 py-3 font-medium">
                  <div className="flex items-center gap-2">
                    {p.name}
                    {p.origin === "campaign" && (
                      <span className="badge bg-brand-50 text-brand-700" title="Came from a campaign lead">
                        <Send className="h-3 w-3 mr-0.5" /> Campaign
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-ink-700">
                  {p.phone && <div>{p.phone}</div>}
                  {p.email && <div className="text-ink-500 text-xs">{p.email}</div>}
                </td>
                <td className="px-4 py-3 text-ink-700">{p.source ?? "—"}</td>
                <td className="px-4 py-3"><span className="badge bg-ink-100 text-ink-700">{stageLabel(p.pathway_stage)}</span></td>
                <td className="px-4 py-3"><FaithChip id={p.faith_status} /></td>
                <td className="px-4 py-3"><DbsChip id={p.dbs_type} /></td>
                <td className="px-4 py-3 text-ink-700">{p.group_name ?? "—"}</td>
                <td className="px-4 py-3 text-right">
                  <button className="text-ink-500 hover:text-brand text-xs" onClick={(e) => { e.stopPropagation(); setEditing(p); }}>Edit</button>
                </td>
              </tr>
            ))}
            {people.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-ink-400">No people match your filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-2">
        {people.map((p) => (
          <button
            key={p.id}
            onClick={() => setEditing(p)}
            className="w-full card p-4 text-left active:bg-ink-50 transition"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-ink-900 truncate">{p.name}</div>
                {p.source && <div className="text-xs text-ink-500 truncate mt-0.5">{p.source}</div>}
              </div>
              <span className="badge bg-ink-100 text-ink-700 shrink-0">{stageLabel(p.pathway_stage)}</span>
            </div>
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              <FaithChip id={p.faith_status} />
              {p.dbs_type && <DbsChip id={p.dbs_type} />}
              {p.group_name && <span className="badge bg-ink-100 text-ink-700">{p.group_name}</span>}
              {p.origin === "campaign" && (
                <span className="badge bg-brand-50 text-brand-700">
                  <Send className="h-3 w-3 mr-0.5" /> Campaign
                </span>
              )}
            </div>
            {(p.phone || p.email) && (
              <div className="mt-2.5 text-xs text-ink-500 flex items-center gap-3 flex-wrap">
                {p.phone && <a href={`tel:${p.phone}`} onClick={(e) => e.stopPropagation()} className="hover:text-brand">{p.phone}</a>}
                {p.email && <a href={`mailto:${p.email}`} onClick={(e) => e.stopPropagation()} className="hover:text-brand truncate">{p.email}</a>}
              </div>
            )}
          </button>
        ))}
        {people.length === 0 && (
          <div className="card p-8 text-center text-ink-400 text-sm">No people match your filters.</div>
        )}
      </div>

      <Modal open={adding} onClose={() => setAdding(false)} title="Add Person">
        <PersonForm groups={groups} leaders={leaders} onCreated={() => { setAdding(false); load(); }} onCancel={() => setAdding(false)} />
      </Modal>
      <Modal open={!!editing} onClose={() => setEditing(null)} title="Edit Person">
        {editing && (
          <PersonForm
            groups={groups}
            leaders={leaders}
            initial={editing}
            onCreated={() => { setEditing(null); load(); }}
            onCancel={() => setEditing(null)}
            onDelete={async () => {
              if (!confirm("Delete this person?")) return;
              await api.del(`/people/${editing.id}`);
              setEditing(null); load();
            }}
          />
        )}
      </Modal>
    </div>
  );
}

function Mini({ label, value, accent = "text-ink-900", icon }: { label: string; value: number; accent?: string; icon?: React.ReactNode }) {
  return (
    <div className="card p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className={`text-2xl font-semibold ${accent}`}>{value}</div>
          <div className="text-xs text-ink-500 mt-1">{label}</div>
        </div>
        {icon && <div className="text-ink-300">{icon}</div>}
      </div>
    </div>
  );
}

function OriginPill({
  active, onClick, label, tint,
}: { active: boolean; onClick: () => void; label: string; tint?: string }) {
  return (
    <button
      data-active={active}
      onClick={onClick}
      className={`px-3 py-1 rounded-full ring-1 text-xs font-medium transition ${
        tint ?? "ring-ink-200 text-ink-700 data-[active=true]:bg-ink-900 data-[active=true]:text-white data-[active=true]:ring-ink-900"
      }`}
    >
      {label}
    </button>
  );
}

function FaithChip({ id }: { id: string }) {
  const f = FAITH_STATUSES.find((s) => s.id === id);
  if (!f) return <span className="text-ink-400">—</span>;
  return (
    <span className="badge bg-ink-50 text-ink-700">
      <span className={`h-1.5 w-1.5 rounded-full mr-1 ${f.dot}`} />{f.label}
    </span>
  );
}

export function DbsChip({ id }: { id: string | null | undefined }) {
  const d = DBS_TYPES.find((t) => t.id === id);
  if (!d) return <span className="text-ink-300 text-xs">—</span>;
  return <span className={`badge ${d.chip}`}><span className={`h-1.5 w-1.5 rounded-full mr-1 ${d.dot}`} />{d.short}</span>;
}

// Date helpers — baptised_at is stored as a unix epoch in seconds.
function epochToInputDate(ts: number | null | undefined): string {
  if (!ts) return "";
  const d = new Date(ts * 1000);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function inputDateToEpoch(s: string): number | null {
  if (!s) return null;
  const t = new Date(s + "T00:00:00").getTime();
  return Number.isFinite(t) ? Math.floor(t / 1000) : null;
}

export function PersonForm({
  groups, leaders = [], initial, onCreated, onCancel, onDelete,
}: {
  groups: Group[];
  leaders?: Leader[];
  initial?: Partial<Person>;
  onCreated: () => void;
  onCancel: () => void;
  onDelete?: () => void;
}) {
  const [form, setForm] = useState<any>({
    name: initial?.name ?? "",
    email: initial?.email ?? "",
    phone: initial?.phone ?? "",
    source: initial?.source ?? "",
    pathway_stage: initial?.pathway_stage ?? "outreach",
    faith_status: initial?.faith_status ?? "exploring",
    group_id: initial?.group_id ?? "",
    assigned_leader_id: initial?.assigned_leader_id ?? "",
    dbs_type: initial?.dbs_type ?? "",
    oikos: initial?.oikos ?? "",
    introduced_by_person_id: initial?.introduced_by_person_id ?? "",
    baptised_at_input: epochToInputDate(initial?.baptised_at),
    baptism_story: initial?.baptism_story ?? "",
    notes: initial?.notes ?? "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [allPeople, setAllPeople] = useState<{ id: string; name: string }[]>([]);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  // Fetch the org's people once so we can populate the "introduced by" picker.
  // Excludes the person being edited (a person can't introduce themselves).
  useEffect(() => {
    api.get<{ people: { id: string; name: string }[] }>("/people").then((r) => {
      const editingId = (initial as Person | undefined)?.id;
      setAllPeople(r.people.filter((p) => p.id !== editingId));
    }).catch(() => {});
  }, [(initial as Person | undefined)?.id]);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const payload: any = {
        ...form,
        group_id: form.group_id || null,
        assigned_leader_id: form.assigned_leader_id || null,
        dbs_type: form.dbs_type || null,
        introduced_by_person_id: form.introduced_by_person_id || null,
        baptised_at: inputDateToEpoch(form.baptised_at_input),
      };
      delete payload.baptised_at_input;
      if (initial && (initial as Person).id) {
        await api.patch(`/people/${(initial as Person).id}`, payload);
      } else {
        await api.post("/people", payload);
      }
      onCreated();
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const showBaptism = form.faith_status === "christian_baptised" || !!form.baptised_at_input;

  return (
    <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="label">Name</label>
          <input className="input" required autoCapitalize="words" autoComplete="name" value={form.name} onChange={(e) => set("name", e.target.value)} />
        </div>
        <div>
          <label className="label">Source</label>
          <input className="input" placeholder="e.g. University Outreach" value={form.source} onChange={(e) => set("source", e.target.value)} />
        </div>
        <div>
          <label className="label">Phone</label>
          <input className="input" type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
        </div>
        <div>
          <label className="label">Email</label>
          <input className="input" type="email" inputMode="email" autoComplete="email" autoCapitalize="off" value={form.email} onChange={(e) => set("email", e.target.value)} />
        </div>
        <div>
          <label className="label">Pathway Stage</label>
          <select className="input" value={form.pathway_stage} onChange={(e) => set("pathway_stage", e.target.value)}>
            {STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Faith Status</label>
          <select className="input" value={form.faith_status} onChange={(e) => set("faith_status", e.target.value)}>
            {FAITH_STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Group</label>
          <select className="input" value={form.group_id ?? ""} onChange={(e) => set("group_id", e.target.value)}>
            <option value="">— None —</option>
            {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Assigned leader</label>
          <select className="input" value={form.assigned_leader_id ?? ""} onChange={(e) => set("assigned_leader_id", e.target.value)}>
            <option value="">— None —</option>
            {leaders.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="label">DBS Type</label>
          <select className="input" value={form.dbs_type ?? ""} onChange={(e) => set("dbs_type", e.target.value)}>
            <option value="">— Not set —</option>
            {DBS_TYPES.map((d) => <option key={d.id} value={d.id}>{d.label} — {d.blurb}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="label">Profile notes</label>
          <textarea className="input min-h-[80px]" value={form.notes ?? ""} onChange={(e) => set("notes", e.target.value)} />
        </div>
      </div>

      {/* Relational + milestone tracking — Oikos, who introduced them, baptism */}
      <div className="pt-2 border-t border-ink-100">
        <div className="text-xs font-semibold uppercase tracking-wide text-ink-500 mt-3 mb-2">Relational network &amp; milestones</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className="label">Oikos <span className="text-ink-400 font-normal">(their relational network — family, work, sport, friends)</span></label>
            <textarea className="input min-h-[60px]" placeholder="e.g. Mum, two younger brothers, footy team, work crew at the cafe…"
              value={form.oikos ?? ""} onChange={(e) => set("oikos", e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className="label">Introduced by</label>
            <select className="input" value={form.introduced_by_person_id ?? ""} onChange={(e) => set("introduced_by_person_id", e.target.value)}>
              <option value="">— No one in the platform yet —</option>
              {allPeople.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <p className="text-xs text-ink-500 mt-1">Track who brought them into the movement so we can see multiplication chains.</p>
          </div>
          <div>
            <label className="label">Baptism date</label>
            <input
              className="input"
              type="date"
              value={form.baptised_at_input}
              onChange={(e) => set("baptised_at_input", e.target.value)}
            />
            {!showBaptism && <p className="text-xs text-ink-500 mt-1">Optional unless faith status is "Existing Christian (Baptised)".</p>}
          </div>
          <div>
            <label className="label">Baptism story</label>
            <input
              className="input"
              placeholder="Brief — where, with whom, what changed"
              value={form.baptism_story ?? ""}
              onChange={(e) => set("baptism_story", e.target.value)}
            />
          </div>
        </div>
      </div>

      {err && <div className="text-sm text-brand">{err}</div>}
      <div className="flex justify-between pt-2">
        <div>
          {onDelete && <button type="button" onClick={onDelete} className="text-brand text-sm">Delete</button>}
        </div>
        <div className="flex gap-2">
          <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="btn-primary" disabled={busy}>{busy ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </form>
  );
}
